import { useState, useEffect } from 'react'
import { Dialog, DialogTitle, DialogDescription, DialogBody, DialogActions } from '@/components/catalyst-ui/dialog'
import { Button } from '@/components/catalyst-ui/button'
import { Input } from '@/components/catalyst-ui/input'
import { Checkbox } from '@/components/catalyst-ui/checkbox'
import { XMarkIcon } from '@heroicons/react/24/solid'

export default function DeleteDomainDialog({
  open,
  onClose,
  domainName,
  onConfirm,
  isLoading = false,
  requireConfirmations = true,
}) {
  const [confirmText, setConfirmText] = useState('')
  const [confirmations, setConfirmations] = useState({
    projectConnection: false,
    dnsChecks: false,
    teamAccess: false,
    cannotUndo: false,
  })

  useEffect(() => {
    if (!open) {
      setConfirmText('')
      setConfirmations({
        projectConnection: false,
        dnsChecks: false,
        teamAccess: false,
        cannotUndo: false,
      })
    }
  }, [open])

  const handleClose = () => {
    setConfirmText('')
    setConfirmations({
      projectConnection: false,
      dnsChecks: false,
      teamAccess: false,
      cannotUndo: false,
    })
    onClose()
  }

  const isConfirmDisabled = requireConfirmations
    ? isLoading ||
    confirmText.trim() !== String(domainName || '').trim() ||
    !confirmations.projectConnection ||
    !confirmations.dnsChecks ||
    !confirmations.teamAccess ||
    !confirmations.cannotUndo
    : isLoading || confirmText.trim() !== String(domainName || '').trim()

  return (
    <Dialog
      size="lg"
      className="!rounded-xl p-5"
      open={open}
      onClose={handleClose}
    >
      <div className="flex items-center justify-between">
        <DialogTitle className="!text-lg !font-semibold !text-[#09090B]">Delete Domain</DialogTitle>
        <button
          onClick={handleClose}
          className="text-black hover:text-zinc-600 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 rounded-full"
        >
          <XMarkIcon className="w-5 h-5 -mt-6 -mr-2.5" />
        </button>
      </div>
      <DialogBody className="!mt-4 space-y-4">
        <DialogDescription className="!text-sm !font-medium !text-[#71717A]">
          Are you sure you want to delete <strong>{domainName}</strong>? Once it&apos;s gone, you won&apos;t be able to recover it.
        </DialogDescription>

        {requireConfirmations && (
          <div className="rounded-md bg-[#F4F4F5] p-3">
            <p className="text-sm font-medium text-[#71717A] mb-2">I understand that:</p>
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={confirmations.projectConnection}
                  onChange={(checked) =>
                    setConfirmations(prev => ({
                      ...prev,
                      projectConnection: checked,
                    }))
                  }
                />
                <span className="text-sm font-semibold text-[#09090B]">
                  Any associated project connection will be removed
                </span>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={confirmations.dnsChecks}
                  onChange={(checked) =>
                    setConfirmations(prev => ({
                      ...prev,
                      dnsChecks: checked,
                    }))
                  }
                />
                <span className="text-sm font-semibold text-[#09090B]">
                  DNS checks and verification for this domain will stop
                </span>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={confirmations.teamAccess}
                  onChange={(checked) =>
                    setConfirmations(prev => ({
                      ...prev,
                      teamAccess: checked,
                    }))
                  }
                />
                <span className="text-sm font-semibold text-[#09090B]">
                  Team members will no longer see this domain
                </span>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={confirmations.cannotUndo}
                  onChange={(checked) =>
                    setConfirmations(prev => ({
                      ...prev,
                      cannotUndo: checked,
                    }))
                  }
                />
                <span className="text-sm font-semibold text-[#09090B]">
                  This action cannot be undone
                </span>
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Type <span className="text-[#b91c1c]">'{domainName || 'domain-name'}'</span> to confirm <span className="text-[#b91c1c]">*</span>
          </label>
          <Input
            placeholder={domainName || 'domain-name'}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full"
          />
        </div>
      </DialogBody>
      <DialogActions className="!mt-4 !flex !flex-col sm:!flex-row !gap-3">
        <Button
          outline
          onClick={handleClose}
          className="w-full sm:flex-1 cursor-pointer !text-base data-focus:!ring-2 data-focus:!ring-blue-500 data-focus:!ring-offset-2"
        >
          Cancel
        </Button>
        <Button
          outline
          onClick={onConfirm}
          disabled={isConfirmDisabled}
          className="w-full sm:flex-1 cursor-pointer !border !border-[#EF4444] !text-[#B91C1C] !bg-[#FEE2E2] !text-base !font-semibold hover:!bg-red-50 !focus-visible:!outline !focus-visible:!outline-2 !focus-visible:!outline-offset-2 !focus-visible:!outline-blue-500"
          style={{ boxShadow: '0px 4px 6px -1px #0000001A, 0px 2px 4px -2px #0000001A' }}
        >
          {isLoading ? 'Deleting...' : 'Delete Domain'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
